import cx from 'classnames'
import type { CSSProperties, ReactNode } from 'react'

export interface SkeletonAvatarProps {
  prefixCls?: string
  className?: string
  style?: CSSProperties
  size?: 'large' | 'small' | 'default' | number
  shape?: 'circle' | 'square'
}

export interface SkeletonTitleProps {
  prefixCls?: string
  className?: string
  style?: CSSProperties
  width?: number | string
}

type WidthUnit = number | string

export interface SkeletonParagraphProps {
  prefixCls?: string
  className?: string
  style?: CSSProperties
  width?: WidthUnit | WidthUnit[]
  rows?: number
}

export interface SkeletonProps {
  active?: boolean
  loading?: boolean
  prefixCls?: string
  className?: string
  children?: ReactNode
  avatar?: SkeletonAvatarProps | boolean
  title?: SkeletonTitleProps | boolean
  paragraph?: SkeletonParagraphProps | boolean
}

const PREFIX = 'ant-skeleton'

const getComponentProps = <T,>(prop: T | boolean | undefined): T => {
  if (prop && typeof prop === 'object') {
    return prop as T
  }
  return {} as T
}

const getAvatarBasicProps = (hasTitle: boolean, hasParagraph: boolean): SkeletonAvatarProps => {
  if (hasTitle && !hasParagraph) {
    return { shape: 'square' }
  }
  return { shape: 'circle' }
}

const getTitleBasicProps = (hasAvatar: boolean, hasParagraph: boolean): SkeletonTitleProps => {
  if (!hasAvatar && hasParagraph) {
    return { width: '38%' }
  }
  if (hasAvatar && hasParagraph) {
    return { width: '50%' }
  }
  return {}
}

const getParagraphBasicProps = (hasAvatar: boolean, hasTitle: boolean): SkeletonParagraphProps => {
  const basicProps: SkeletonParagraphProps = {}
  if (!hasAvatar || !hasTitle) {
    basicProps.width = '61%'
  }
  if (!hasAvatar && hasTitle) {
    basicProps.rows = 3
  } else {
    basicProps.rows = 2
  }
  return basicProps
}

const SkeletonAvatar = ({ prefixCls, className, style, size = 'large', shape }: SkeletonAvatarProps) => {
  const sizeCls = cx({
    [`${prefixCls}-lg`]: size === 'large',
    [`${prefixCls}-sm`]: size === 'small',
  })
  const shapeCls = cx({
    [`${prefixCls}-circle`]: shape === 'circle',
    [`${prefixCls}-square`]: shape === 'square',
  })
  const sizeStyle: CSSProperties =
    typeof size === 'number' ? { width: size, height: size, lineHeight: `${size}px` } : {}
  return <span className={cx(prefixCls, className, sizeCls, shapeCls)} style={{ ...sizeStyle, ...style }} />
}

const SkeletonTitle = ({ prefixCls, className, width, style }: SkeletonTitleProps) => (
  // eslint-disable-next-line jsx-a11y/heading-has-content -- empty <h3> mirrors AntD Skeleton's exact DOM
  <h3 className={cx(prefixCls, className)} style={{ width, ...style }} />
)

const SkeletonParagraph = ({ prefixCls, className, style, width, rows = 2 }: SkeletonParagraphProps) => {
  const getWidth = (index: number): WidthUnit | undefined => {
    if (Array.isArray(width)) {
      return width[index]
    }
    if (rows - 1 === index) {
      return width
    }
    return undefined
  }
  const rowList = [...Array(rows)].map((_, index) => <li key={index} style={{ width: getWidth(index) }} />)
  return (
    <ul className={cx(prefixCls, className)} style={style}>
      {rowList}
    </ul>
  )
}

export const Skeleton = (props: SkeletonProps) => {
  const { loading, className, children, avatar = false, title = true, paragraph = true, active } = props

  // Original AntD: render children only when `loading` is explicitly false; if `loading` is omitted, render the skeleton.
  if ('loading' in props && !loading) {
    return <>{children}</>
  }

  const hasAvatar = !!avatar
  const hasTitle = !!title
  const hasParagraph = !!paragraph

  let avatarNode: ReactNode = null
  if (hasAvatar) {
    const avatarProps: SkeletonAvatarProps = {
      prefixCls: `${PREFIX}-avatar`,
      ...getAvatarBasicProps(hasTitle, hasParagraph),
      ...getComponentProps<SkeletonAvatarProps>(avatar),
    }
    avatarNode = (
      <div className={`${PREFIX}-header`}>
        <SkeletonAvatar {...avatarProps} />
      </div>
    )
  }

  let contentNode: ReactNode = null
  if (hasTitle || hasParagraph) {
    let titleNode: ReactNode = null
    if (hasTitle) {
      const titleProps: SkeletonTitleProps = {
        prefixCls: `${PREFIX}-title`,
        ...getTitleBasicProps(hasAvatar, hasParagraph),
        ...getComponentProps<SkeletonTitleProps>(title),
      }
      titleNode = <SkeletonTitle {...titleProps} />
    }

    let paragraphNode: ReactNode = null
    if (hasParagraph) {
      const paragraphProps: SkeletonParagraphProps = {
        prefixCls: `${PREFIX}-paragraph`,
        ...getParagraphBasicProps(hasAvatar, hasTitle),
        ...getComponentProps<SkeletonParagraphProps>(paragraph),
      }
      paragraphNode = <SkeletonParagraph {...paragraphProps} />
    }

    contentNode = (
      <div className={`${PREFIX}-content`}>
        {titleNode}
        {paragraphNode}
      </div>
    )
  }

  const cls = cx(PREFIX, className, {
    [`${PREFIX}-with-avatar`]: hasAvatar,
    [`${PREFIX}-active`]: active,
  })

  return (
    <div className={cls}>
      {avatarNode}
      {contentNode}
    </div>
  )
}

export default Skeleton
